import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Button, IconArrowLeft, Spinner } from '@timely/ui';
import { ledgerBillWords, ledgerSide, ledgerView, type LedgerView } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { paths } from '../workspace/navigation';
import { useCustomerDetail } from '../book/useBook';
import { useFormatters } from '../book/useFormatters';
import { useCompanyProfile } from '../branding/useCompanyProfile';
import { useLogoUrl } from '../branding/useLogo';
import { useDocumentTitle } from '../lib/documentTitle';
import { exportLedgerCsv, mayExportStatement } from '../receivables/statementAccess';
import type { LedgerResult } from '../receivables/useLedger';
import { ExportRefused, Letterhead, PaperLine, PaperTd, PaperTh, PaperTotal, usePaper } from './CustomerStatement';

/**
 * A customer's ledger as a statement of account for them (0038; docs/product/statement/01-LEDGER-PLAN.md §8): the
 * company's own letterhead, who it is for, the period, the opening balance, every voucher with its debit or credit
 * and the balance after it, and the closing balance — TallyPrime's figures, to the paisa, as read on request.
 *
 * Printed, or saved as a PDF, by the browser's own print dialog, so a name in any script prints as it should (as
 * Phase 1's outstanding statement). It is an export: without "Export reports" the address says so and reads nothing
 * (statementAccess.ts). It shows a ledger read for this account in the last fifteen minutes, and nothing older:
 * the answer is not kept beyond that, so a statement is always of a ledger read just now. No payment details: no
 * narration, no bank, nothing of how the customer paid.
 */
export function CustomerLedgerStatement() {
    const context = useActiveWorkspace();
    // Refused before anything is read: the right is checked here, not only by hiding the button that leads here.
    return mayExportStatement(context) ? <LedgerStatementPage /> : <ExportRefused />;
}

type Kept = { state: 'loading' } | { state: 'ok'; result: LedgerResult } | { state: 'gone' };

function LedgerStatementPage() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const { customerId = '' } = useParams();
    const [params] = useSearchParams();
    const request = params.get('request') ?? '';
    const { result, loading } = useCustomerDetail(context, customerId);
    const company = useCompanyProfile(workspace.id);
    const logo = useLogoUrl(workspace.logoPath ?? null);
    const { date } = useFormatters();
    const [kept, setKept] = useState<Kept>({ state: 'loading' });
    usePaper();

    useEffect(() => {
        let live = true;
        if (!/^[0-9a-f-]{36}$/.test(request)) { setKept({ state: 'gone' }); return; }
        void supabase().rpc('ledger_read', { p_workspace: workspace.id, p_request: request }).then(({ data, error }) => {
            if (!live) return;
            const r = data as { customer: string; status: string; from: string; to: string; answer: Omit<LedgerResult, 'from' | 'to'> | null } | null;
            // Only this account's ledger, while it is kept.
            setKept(!error && r && r.customer === customerId && r.status === 'completed' && r.answer ? { state: 'ok', result: { ...r.answer, from: r.from, to: r.to } } : { state: 'gone' });
        });
        return () => { live = false; };
    }, [workspace.id, request, customerId]);

    const to = paths(workspace.slug);
    const customer = result?.status === 'ok' ? result.detail.customer : null;
    const view = useMemo(() => (kept.state === 'ok' ? ledgerView(kept.result) : null), [kept]);
    // The suggested file name when it is saved as a PDF.
    useDocumentTitle(customer && view ? `${customer.company} statement of account ${view.from} to ${view.to}` : 'Statement of account');
    const waiting = loading || !result || company.loading || kept.state === 'loading';

    return (
        <div className="min-h-dvh bg-bg print:bg-card">
            <style>{'@page { size: A4; margin: 12mm 12mm 14mm; }'}</style>
            <div className="print:hidden sticky top-0 z-10 border-b border-separator bg-card">
                <div className="mx-auto max-w-[210mm] px-4 py-2.5 flex items-center justify-between gap-x-4 gap-y-2 flex-wrap">
                    <Link to={customer ? to.customerTab(customer.id, 'ledger') : to.customers}
                        className="inline-flex items-center gap-1.5 text-secondary font-semibold text-accent-text hover:underline">
                        <IconArrowLeft size={14} />{customer ? 'Back to the ledger' : 'Back to customers'}
                    </Link>
                    {customer && view && !waiting && (
                        <div className="flex items-center gap-2 flex-wrap">
                            <Button size="sm" variant="secondary" onClick={() => exportLedgerCsv(context, view, customer.company)}>Excel (CSV)</Button>
                            <Button size="sm" variant="primary" onClick={() => window.print()}>Print or save as PDF</Button>
                        </div>
                    )}
                </div>
                {customer && view && !waiting && (
                    <p className="mx-auto max-w-[210mm] px-4 pb-2.5 text-caption text-label-3">
                        To keep it as a PDF, choose “Save as PDF” as the printer in the print dialog.
                    </p>
                )}
            </div>

            <main className="mx-auto max-w-[210mm] sm:my-6 print:my-0 bg-card sm:rounded-card sm:shadow-e1 print:shadow-none print:rounded-none px-5 py-6 sm:px-[14mm] sm:py-[12mm] print:p-0">
                {waiting || !result ? (
                    <div className="py-20 grid place-items-center" role="status"><span className="sr-only">Preparing the statement of account</span><Spinner className="w-6 h-6 text-accent-text" /></div>
                ) : result.status !== 'ok' || !customer ? (
                    <div role="alert" className="py-10 text-center">
                        <h1 className="text-title font-semibold text-label">That account is not available</h1>
                        <p className="mt-3 text-body text-label-2 max-w-md mx-auto">
                            It may belong to another company, it may not be one of yours to work, or it may no longer exist.
                        </p>
                    </div>
                ) : !view || kept.state !== 'ok' ? (
                    <div role="alert" className="py-10 text-center">
                        <h1 className="text-title font-semibold text-label">This ledger is no longer kept</h1>
                        <p className="mt-3 text-body text-label-2 max-w-md mx-auto">
                            A ledger read from TallyPrime is kept for fifteen minutes. Read it again on the account’s Ledger, then print it from there.
                        </p>
                        <Link to={to.customerTab(customerId, 'ledger')} className="mt-6 inline-flex items-center gap-1.5 text-secondary font-semibold text-accent-text hover:underline">
                            <IconArrowLeft size={14} />Back to the ledger
                        </Link>
                    </div>
                ) : (
                    <LedgerDocument view={view} readAt={kept.result.readAt} customer={customer} profile={company.profile} companyName={workspace.name} logo={logo} prepared={date.todayValue()} />
                )}
            </main>
        </div>
    );
}

function LedgerDocument({ view, readAt, customer, profile, companyName, logo, prepared }: {
    view: LedgerView;
    readAt: string;
    customer: { company: string; address?: string | null; city?: string | null; state?: string | null; taxId?: string | null };
    profile: Parameters<typeof Letterhead>[0]['profile'];
    companyName: string;
    logo: string | null;
    prepared: string;
}) {
    const { money, date, count } = useFormatters();
    const theirPlace = [customer.city, customer.state].filter(Boolean).join(', ');
    const amount = (paise: number) => (paise ? money.precise(paise / 100) : '');
    const balance = (paise: number) => `${money.precise(Math.abs(paise) / 100)}${ledgerSide(paise) ? ` ${ledgerSide(paise)}` : ''}`;
    return (
        <article aria-label={`Statement of account for ${customer.company}`} className="text-label">
            <header className="flex items-start justify-between gap-x-8 gap-y-5 flex-wrap">
                <Letterhead profile={profile} companyName={companyName} logo={logo} />
                <div className="sm:text-right">
                    <h1 className="font-display stretch-title text-title font-semibold tracking-title leading-tight">Statement of account</h1>
                    <p className="mt-1 text-secondary text-label-2"><span className="num">{date.day(view.from)}</span> to <span className="num">{date.day(view.to)}</span></p>
                    <p className="text-caption text-label-3">Prepared on <span className="num">{date.day(prepared)}</span></p>
                </div>
            </header>

            <div className="mt-8 grid gap-x-10 gap-y-6 sm:grid-cols-2 items-start">
                <section aria-label="For">
                    <p className="text-caption font-semibold uppercase tracking-caps text-label-3">To</p>
                    <p className="mt-1 text-subhead font-semibold break-words">{customer.company}</p>
                    <div className="text-secondary text-label-2 leading-snug">
                        {customer.address && <p className="break-words">{customer.address}</p>}
                        {theirPlace && <p>{theirPlace}</p>}
                        {customer.taxId && <p>{profile.taxIdLabel}: <span className="num">{customer.taxId}</span></p>}
                    </div>
                </section>
                <section aria-label="Summary">
                    <p className="text-caption font-semibold uppercase tracking-caps text-label-3">Summary</p>
                    <dl className="mt-1">
                        <PaperLine label={`Opening balance, ${date.day(view.from)}`} value={balance(view.openingPaise)} />
                        <PaperLine label="Debits" value={money.precise(view.debitsPaise / 100)} />
                        <PaperLine label="Credits" value={money.precise(view.creditsPaise / 100)} />
                        <PaperLine strong label={view.closingPaise < 0 ? `Balance in your favour, ${date.day(view.to)}` : `Balance due, ${date.day(view.to)}`} value={balance(view.closingPaise)} />
                    </dl>
                </section>
            </div>

            <section aria-label="Entries" className="mt-8">
                <h2 className="text-secondary font-semibold">Entries, oldest first <span className="font-normal text-label-3">({count(view.rows.length)})</span></h2>
                <table className="mt-2 w-full border-collapse text-caption">
                    <thead>
                        <tr className="text-label-3">
                            <PaperTh>Date</PaperTh>
                            <PaperTh>Particulars</PaperTh>
                            <PaperTh wide>No.</PaperTh>
                            <PaperTh wide>Bills</PaperTh>
                            <PaperTh numeric wide>Debit</PaperTh>
                            <PaperTh numeric wide>Credit</PaperTh>
                            <PaperTh numeric>Balance</PaperTh>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="break-inside-avoid">
                            <PaperTd className="num whitespace-nowrap">{date.day(view.from)}</PaperTd>
                            <PaperTd className="font-semibold">Opening balance</PaperTd>
                            <PaperTd wide>{''}</PaperTd>
                            <PaperTd wide>{''}</PaperTd>
                            <PaperTd numeric wide>{''}</PaperTd>
                            <PaperTd numeric wide>{''}</PaperTd>
                            <PaperTd numeric>{balance(view.openingPaise)}</PaperTd>
                        </tr>
                        {view.rows.map((r, i) => (
                            <tr key={`${r.date}:${r.number ?? ''}:${i}`} className="break-inside-avoid">
                                <PaperTd className="num whitespace-nowrap">{date.day(r.date)}</PaperTd>
                                <PaperTd className="break-words">
                                    {r.type}
                                    {/* A phone's screen has room for three columns: No., bills and the entry's debit or credit go under
                                        the voucher. Paper, and a wider screen, have room for all seven. */}
                                    <span className="block sm:hidden print:hidden text-label-3">{[r.number ? `No. ${r.number}` : '', ledgerBillWords(r.bills)].filter(Boolean).join(' · ')}</span>
                                    <span className="block sm:hidden print:hidden num text-label">
                                        {r.debitPaise ? `Debit ${money.precise(r.debitPaise / 100)}` : r.creditPaise ? `Credit ${money.precise(r.creditPaise / 100)}` : ''}
                                    </span>
                                </PaperTd>
                                <PaperTd wide className="num whitespace-nowrap">{r.number ?? ''}</PaperTd>
                                <PaperTd wide className="break-words">{ledgerBillWords(r.bills)}</PaperTd>
                                <PaperTd numeric wide>{amount(r.debitPaise)}</PaperTd>
                                <PaperTd numeric wide>{amount(r.creditPaise)}</PaperTd>
                                <PaperTd numeric>{balance(r.balancePaise)}</PaperTd>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <PaperTotal label="Total debits" value={money.precise(view.debitsPaise / 100)} />
                <PaperTotal label="Total credits" value={money.precise(view.creditsPaise / 100)} />
                <PaperTotal label={`Closing balance, ${date.day(view.to)}`} value={balance(view.closingPaise)} />
            </section>

            <footer className="mt-10 pt-4 border-t border-separator text-caption text-label-2 leading-relaxed break-inside-avoid">
                <p>Figures as in our books, read on <span className="num">{date.day(readAt)}</span>. Dr is owed to us; Cr is held for you. Please check this statement against your records and tell us of any difference.</p>
                {profile.footerNote && <p className="mt-2 whitespace-pre-line">{profile.footerNote}</p>}
            </footer>
        </article>
    );
}
